import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { Space } from '@client/components/_ui/layout/Space';
import { isWhereClauseEmpty } from '@client/components/filters/core/where-builder';
import { applyMyWorkOwnerFilter } from '@client/components/workload-manager/my-work-owner-filter';
import { WorkloadManagerFilters } from '@client/components/workload-manager/WorkloadManagerFilters';
import {
    type WorkloadItemKey,
    WorkloadManagerReassignmentDrawer,
} from '@client/components/workload-manager/WorkloadManagerReassignmentDrawer';
import { WorkloadManagerTable } from '@client/components/workload-manager/WorkloadManagerTable';
import { usePermissions } from '@client/hooks/auth/usePermissions';
import { useTrackEngagementView } from '@client/hooks/business/engagement/useTrackEngagementView';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useUserId } from '@client/hooks/business/user/useUserId';
import { getPreviousPathname } from '@client/hooks/navigation/previous-pathname';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { TableEmptyState } from '@client/hooks/table-v2';
import { useFeatureFlag } from '@client/hooks/useFeatureFlag';
import { ENABLE_WORKLOAD_MY_WORK_DEFAULT_FLAG } from '@shared/constants/business/workload';
import { FilterOperatorSchema } from '@shared/schemas/filter';
import { createFileRoute, Outlet, useLocation } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Switch } from 'antd';
import { useMemo, useState } from 'react';
import { z } from 'zod';
import { useWorkloadDeepLinkWhere, type WorkloadWhere } from './workload-deep-link';

const WorkloadFilterSchema = z.union([
    // Branch for owner-ids: coerce value to BigInt
    z.object({
        id: z.string(),
        filterId: z.literal('owner-ids'),
        operator: FilterOperatorSchema,
        value: z.union([z.array(z.coerce.bigint()), z.coerce.bigint(), z.null()]),
    }),
    // General branch for other filters
    z.object({
        id: z.string(),
        filterId: z.string(),
        operator: FilterOperatorSchema,
        value: z.any(),
    }),
]);

const WorkloadSearchSchema = z.object({
    'workload-filters': z.array(WorkloadFilterSchema).optional().catch(undefined),
});

export const Route = createFileRoute('/console/$orgId/workload-manager/tasks/list')({
    staticData: {
        metadata: {
            title: 'Task list',
        },
        breadcrumb: {
            title: 'Tasks',
        },
    },
    validateSearch: zodValidator(WorkloadSearchSchema),
    component: TaskList,
});

function TaskList() {
    useTrackEngagementView('workload_tab_viewed', { tab: 'tasks' });
    const { can } = usePermissions();
    const canAdminOrg = can('org:admin');
    const orgId = useOrgId();
    const searchParams = Route.useSearch();
    const { pathname } = useLocation();

    const [workloadFilters, setWorkloadFilters] = usePathState<WorkloadWhere>('workload-filters-where', {});

    // URL deep-link filters (e.g. the Home Tasks card) must shape the FIRST query — without this
    // seed the table fires one unfiltered query and only narrows after the filter bar's 300ms
    // debounce replay lands. A drill-down-and-back keeps restored state, and the first filter-bar
    // change hands ownership back to path state.
    const {
        activeWorkloadWhere: activeWorkloadFilters,
        hasPendingDeepLinkWhere,
        onWorkloadFiltersChange,
    } = useWorkloadDeepLinkWhere({
        urlFilters: searchParams['workload-filters'],
        previousPathname: getPreviousPathname(),
        pathname,
        workloadWhere: workloadFilters,
        setWorkloadWhere: setWorkloadFilters,
    });
    const [selectedRowKeys, setSelectedRowKeys] = useState<WorkloadItemKey[]>([]);
    const [reassignmentDrawerOpen, setReassignmentDrawerOpen] = useState(false);

    // Per-tab "My work" toggle since CUR-363 (the shared WM-level "My work / See all" control is
    // gone); like the Emails tab's "My emails", the toggle is part of the tab's filter state —
    // path state, default on, survives in-app navigation.
    const myWorkFeatureEnabled = useFeatureFlag(ENABLE_WORKLOAD_MY_WORK_DEFAULT_FLAG, false);
    const [myWork, setMyWork] = usePathState<boolean>('workload-my-work', true);
    const isMyWorkScope = myWorkFeatureEnabled && myWork;
    const userId = useUserId();
    const effectiveFilters = useMemo(
        () => applyMyWorkOwnerFilter({ myWorkFeatureEnabled, myWork, userId, filters: activeWorkloadFilters }),
        [myWorkFeatureEnabled, myWork, userId, activeWorkloadFilters],
    );

    return (
        <PageController route={Route}>
            <Outlet />

            <TableToolbar
                filters={
                    <WorkloadManagerFilters
                        initialFilters={searchParams['workload-filters']}
                        onChange={onWorkloadFiltersChange}
                    />
                }
                variant="enhanced"
                actions={
                    <Space size="middle">
                        {myWorkFeatureEnabled && (
                            <div className="flex items-center gap-2">
                                <Switch
                                    size="small"
                                    checked={myWork}
                                    onChange={setMyWork}
                                    aria-label="Show only my work"
                                />
                                <span className="text-muted-foreground text-sm">My work</span>
                            </div>
                        )}
                        {canAdminOrg && (
                            <Button disabled={!selectedRowKeys.length} onClick={() => setReassignmentDrawerOpen(true)}>
                                Reassign selected items
                            </Button>
                        )}
                    </Space>
                }
            />

            <WorkloadManagerReassignmentDrawer
                selectedRowKeys={selectedRowKeys.map((key) => `${key.workItemType}-${key.id}`)}
                open={reassignmentDrawerOpen}
                onClose={({ reassigned }) => {
                    setReassignmentDrawerOpen(false);
                    if (reassigned) {
                        setSelectedRowKeys([]);
                    }
                }}
            />

            <WorkloadManagerTable
                filters={effectiveFilters}
                resetOffsetOnMount={hasPendingDeepLinkWhere}
                features={canAdminOrg ? [{ feature: 'selection', onChange: setSelectedRowKeys, selectedRowKeys }] : []}
                emptyContent={
                    <TableEmptyState
                        // The "My work" toggle is a dataset scope, not a filter — only the filter
                        // bar's predicates flip the copy to the no-matches variant. The scope (and
                        // the table's hard-coded exclusion of completed items) instead get honest
                        // no-records copy: "active" and "assigned to you", never a false "no tasks
                        // exist" claim.
                        hasActiveFilters={!isWhereClauseEmpty(activeWorkloadFilters)}
                        noMatchesTitle="No tasks match your filters"
                        noRecordsTitle={isMyWorkScope ? 'No active tasks assigned to you' : 'No active tasks'}
                        noRecordsDescription={
                            isMyWorkScope
                                ? 'Turn off My work to see all tasks, or create a new one.'
                                : 'Tasks and scheduled work items will appear here when they are created.'
                        }
                        action={
                            <Button.Add.Link
                                to="/console/$orgId/workload-manager/tasks/list/add"
                                params={{ orgId }}
                                search={(prev) => prev}
                                label="New task"
                                variant="primary"
                            />
                        }
                    />
                }
            />
        </PageController>
    );
}
